/** Image attachment sources for brainstorm uploads */
export const IMAGE_SOURCE = {
  FILE: 'file',
  CAMERA: 'camera',
};

export const sourceLabel = (source) =>
  source === IMAGE_SOURCE.CAMERA ? 'Camera' : 'File upload';

/**
 * @param {string} extraInfo
 * @param {{ url: string, source: string }[]} attachments
 */
export function appendBrainstormImagesToExtraInfo(extraInfo, attachments) {
  const items = (attachments || []).filter((a) => a?.url);
  if (items.length === 0) {
    return extraInfo?.trim() || '';
  }

  const imageBlock = items
    .map((item, index) => {
      const kind = sourceLabel(item.source);
      const alt = `${kind} · Brainstorm scan ${index + 1}`;
      return `![${alt}](${item.url})`;
    })
    .join('\n');

  const header = '## Brainstorm notes';
  const base = extraInfo?.trim() || '';

  if (!base) {
    return `${header}\n${imageBlock}`;
  }

  const firstLine = imageBlock.split('\n')[0];
  if (base.includes(firstLine)) {
    return base;
  }

  return `${base}\n\n${header}\n${imageBlock}`;
}

export function hasBrainstormExtraContent(extraInfo, mediumUrl) {
  const text = extraInfo?.trim();
  if (text) return true;
  if (mediumUrl?.trim()) return true;
  return false;
}

export async function uploadQuestionImage(apiClient, file) {
  const formData = new FormData();
  formData.append('file', file);

  const response = await apiClient.post('/api/upload-question-image', formData, {
    transformRequest: [
      (data, headers) => {
        if (data instanceof FormData) {
          delete headers['Content-Type'];
        }
        return data;
      },
    ],
  });

  const url = response.data?.url;
  if (!url) {
    throw new Error(response.data?.error || 'Upload did not return image URL');
  }
  return url;
}

export function parseImageAltSource(alt) {
  if (!alt) return { source: null, caption: alt };
  if (alt.startsWith('Camera ·')) {
    return { source: IMAGE_SOURCE.CAMERA, caption: alt };
  }
  if (alt.startsWith('File upload ·')) {
    return { source: IMAGE_SOURCE.FILE, caption: alt };
  }
  return { source: null, caption: alt };
}

export function buildUploadImageUrl(url) {
  const path = resolveUploadRequestPath(url);
  if (!path) return null;
  const base = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
  if (!base) return path;
  return `${base}${path}`;
}

export function extractMarkdownImages(raw) {
  if (!raw) return { text: '', images: [] };
  const images = [];
  const text = raw.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, (_, alt, url) => {
    images.push({ alt: alt || 'Brainstorm scan', url: url.trim() });
    return '\n';
  });
  return { text, images };
}

/** Markdown images plus mediumUrl when not already listed */
export function mergeExtraInfoImages(extraInfo, mediumUrl) {
  const { images } = extractMarkdownImages(extraInfo || '');
  const paths = new Set(
    images.map((img) => resolveUploadRequestPath(img.url)).filter(Boolean)
  );
  const mediumPath = resolveUploadRequestPath(mediumUrl);
  if (mediumPath && !paths.has(mediumPath)) {
    return [
      { url: mediumUrl, alt: 'File upload · Reference image' },
      ...images,
    ];
  }
  return images;
}

export function resolveUploadRequestPath(url) {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) {
    try {
      return new URL(url).pathname;
    } catch {
      return null;
    }
  }
  if (url.startsWith('/api/uploads/')) {
    return url;
  }
  if (url.startsWith('uploads/')) {
    return `/api/${url}`;
  }
  return `/api/uploads/${url.replace(/^\/+/, '')}`;
}
